'use client';

import { DynamicIcon } from 'phosphor-icons/react';
import { c } from '../../../utils/tailwind-merge';
import { Popover } from '../Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../Tooltip/TooltipRadix';
import type { BadgeProps, BadgePropsContent } from './Badge.types';

const BadgeContent = ({ text, iconName }: BadgePropsContent) => (
  <div className="flex items-center gap-1" aria-label={text}>
    {iconName && (
      <DynamicIcon iconName={iconName} size={16} className="shrink-0" />
    )}
    {text}
  </div>
);

const InteractivePopover = ({
  text,
  iconName,
  content,
}: {
  text: string;
  iconName?: string;
  content: React.ReactNode;
}) => {
  return (
    <Popover>
      <Popover.Trigger
        aria-label={text}
        className="cursor-pointer border-none bg-transparent p-0"
      >
        <BadgeContent text={text} iconName={iconName} />
      </Popover.Trigger>
      <Popover.Content className={c('z-50')}>{content}</Popover.Content>
    </Popover>
  );
};

export const Badge = ({
  text,
  iconName,
  tooltip,
  popover,
  interactive = false,
  className = '',
}: BadgeProps) => {
  return (
    <div
      className={c(
        'inline-block whitespace-nowrap rounded-sm bg-grey-100 px-1 py-0.5 text-xs font-normal tracking-tighter text-grey-700',
        className
      )}
    >
      {!tooltip && !popover ? (
        <BadgeContent text={text} iconName={iconName} />
      ) : interactive || popover ? (
        <InteractivePopover
          text={text}
          iconName={iconName}
          content={popover || tooltip}
        />
      ) : (
        <Tooltip>
          <TooltipProvider>
            <TooltipTrigger aria-label={text}>
              <BadgeContent text={text} iconName={iconName} />
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </TooltipProvider>
        </Tooltip>
      )}
    </div>
  );
};
